import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, Trash2, Phone, Mail, MessageCircle, Clock, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

interface AdminFreelancer {
  id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  location: string;
  work_telephone: string;
  work_line_id: string;
  work_whatsapp: string;
  email: string;
  age: number | null;
  height: string;
  status: string;
  created_at: string;
  photo1_url: string;
  photo2_url: string;
}

export function FreelancersTab({ token }: { token: string }) {
  const [items, setItems] = useState<AdminFreelancer[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/freelancers`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setItems(await res.json());
    } catch (e) { console.error('Failed to fetch freelancers', e); }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const approve = async (id: string) => {
    const res = await fetch(`${API}/api/admin/freelancers/${id}/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) fetchItems();
  };
  const unapprove = async (id: string) => {
    const res = await fetch(`${API}/api/admin/freelancers/${id}/unapprove`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) fetchItems();
  };
  const remove = async (id: string) => {
    if (!window.confirm('Delete this submission permanently?')) return;
    const res = await fetch(`${API}/api/admin/freelancers/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) fetchItems();
  };

  const pendingCount = items.filter(i => i.status === 'pending').length;
  const visible = items.filter(i => filter === 'all' || i.status === filter);
  const photoSrc = (url: string) => `${API}${url}?auth=${token}`;

  return (
    <div className="space-y-4" data-testid="freelancers-tab-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold">Freelancer Submissions</h2>
        <div className="flex gap-2">
          {(['all', 'pending', 'approved'] as const).map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} data-testid={`freelancers-filter-${f}`} className="capitalize">
              {f}{f === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {visible.map(f => (
          <Card key={f.id} data-testid={`freelancer-${f.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-base">{f.display_name}</h3>
                  <Badge variant={f.status === 'approved' ? 'default' : 'secondary'}
                    className={f.status === 'approved' ? 'bg-green-600' : 'bg-amber-500 text-white'}
                    data-testid={`freelancer-status-${f.id}`}>
                    {f.status === 'approved' ? 'Approved' : 'Pending'}
                  </Badge>
                </div>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />{new Date(f.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-1 mb-3">
                <img src={photoSrc(f.photo1_url)} alt="Photo 1" className="w-full aspect-square object-cover rounded-md" data-testid={`freelancer-photo1-${f.id}`} />
                <img src={photoSrc(f.photo2_url)} alt="Photo 2" className="w-full aspect-square object-cover rounded-md" data-testid={`freelancer-photo2-${f.id}`} />
              </div>

              <div className="text-sm space-y-1 text-gray-700">
                <p><span className="text-gray-500">Full name:</span> {f.first_name} {f.last_name}</p>
                {f.location && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-purple-500" />{f.location}</p>}
                {(f.age || f.height) && <p><span className="text-gray-500">Age / Height:</span> {[f.age, f.height].filter(Boolean).join(' · ')}</p>}
                <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />{f.work_telephone}</p>
                {f.work_line_id && <p className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded bg-[#00B900] text-white text-[9px] font-bold">L</span>{f.work_line_id}</p>}
                {f.work_whatsapp && <p className="flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />{f.work_whatsapp}</p>}
                {f.email && <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400" />{f.email}</p>}
              </div>

              <div className="flex gap-2 mt-4">
                {f.status === 'pending' ? (
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 flex-1" onClick={() => approve(f.id)} data-testid={`freelancer-approve-${f.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => unapprove(f.id)} data-testid={`freelancer-unapprove-${f.id}`}>
                    Unpublish
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(f.id)} data-testid={`freelancer-delete-${f.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {visible.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No submissions{filter !== 'all' ? ` (${filter})` : ''} yet.</p>}
      </div>
    </div>
  );
}
